export class SlideLinkGesture {
  constructor() {
    this.pointer = null;
    this.suppressUntil = 0;
  }
  start(id, x, y) {
    this.pointer = { id, x, y, moved: false };
    this.suppressUntil = 0;
  }
  move(id, x, y) {
    if (this.pointer?.id === id && Math.hypot(x - this.pointer.x, y - this.pointer.y) > 7) this.pointer.moved = true;
  }
  finish(id, now, cancelled = false) {
    if (this.pointer?.id !== id) return;
    if (this.pointer.moved || cancelled) this.suppressUntil = now + 600;
    this.pointer = null;
  }
  cancelClick(detail, now) {
    return detail !== 0 && now < this.suppressUntil;
  }
}

export function wrapSlide(index, count) {
  return count > 0 ? ((index % count) + count) % count : 0;
}

// 2.9.1 (Owner): Chromium starts the picture of a cloned image at once, even a lazy one that is hidden, while moving
// an element keeps it waiting. A copy of media whose pictures still wait is therefore parsed in an inert template with
// its files set aside (data-deferred-srcset, data-deferred-src); restoreFiles gives them back when the pictures start.
export function inertCopy(node) {
  const template = document.createElement('template');
  template.innerHTML = node.outerHTML;
  const root = template.content.firstElementChild;
  for (const element of [root, ...root.querySelectorAll('img, source')])
    if (element.matches('img, source'))
      for (const name of ['srcset', 'src'])
        if (element.hasAttribute(name)) {
          element.setAttribute(`data-deferred-${name}`, element.getAttribute(name));
          element.removeAttribute(name);
        }
  return document.importNode(root, true);
}
// A picture's sources come before its image in document order, and an image takes its srcset before its src.
export function restoreFiles(scope) {
  for (const element of scope.querySelectorAll('[data-deferred-srcset], [data-deferred-src]'))
    for (const name of ['srcset', 'src']) {
      const value = element.getAttribute(`data-deferred-${name}`);
      if (value === null) continue;
      element.setAttribute(name, value);
      element.removeAttribute(`data-deferred-${name}`);
    }
}

// A native <video controls> strip owns its own drag (seek bar, volume). A gallery
// swipe started there would navigate away from the video being sought.
const VIDEO_CONTROLS_HEIGHT = 48;
function inVideoControls(event) {
  const video = event.target.closest?.('video[controls]');
  return Boolean(video && event.clientY >= video.getBoundingClientRect().bottom - VIDEO_CONTROLS_HEIGHT);
}

// Pointer Events recognize one swipe; existing arrow navigation owns movement.
// The local touch guard only prevents native
// vertical panning after horizontal intent has locked; pinch/vertical stay native.
export class TrackInput {
  constructor(
    viewport,
    controller,
    { onStart = () => {}, onEnd = () => {}, onNavigate = (d) => controller.move(d), allowVideo = false } = {},
  ) {
    Object.assign(this, { viewport, controller, onStart, onEnd, onNavigate });
    this.abort = new AbortController();
    this.gesture = new SlideLinkGesture();
    const listen = (target, type, handler, options = {}) =>
      target.addEventListener(type, handler, { ...options, signal: this.abort.signal });
    listen(viewport, 'pointerdown', (e) => {
      if (
        !e.isPrimary ||
        e.button !== 0 ||
        (controller.count < 2 && !allowVideo) ||
        e.defaultPrevented ||
        e.target.closest('iframe,model-viewer,[data-shopify-xr],input,select,textarea,[role="slider"]') ||
        (!allowVideo && e.target.closest('video')) ||
        inVideoControls(e)
      )
        return;
      this.onStart();
      this.gesture.start(e.pointerId, e.clientX, e.clientY);
      this.pointer = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        axis: null,
        fired: false,
      };
      this.pointer.generation = controller.generation;
    });
    listen(
      viewport,
      'pointermove',
      (e) => {
        const p = this.pointer;
        if (!p || p.id !== e.pointerId) return;
        this.gesture.move(e.pointerId, e.clientX, e.clientY);
        this.recognize(e.clientX, e.clientY);
      },
      { passive: true },
    );
    listen(
      viewport,
      'touchmove',
      (e) => {
        if (e.touches.length !== 1) {
          if (this.pointer) this.finish(null, true);
          return;
        }
        this.lock(e.touches[0].clientX, e.touches[0].clientY);
        if (this.pointer?.axis === 'x' && e.cancelable) e.preventDefault();
      },
      { passive: false },
    );
    // Cancel swipe intent as soon as a second touch starts, before pointermove.
    listen(
      document,
      'touchstart',
      (event) => {
        if (event.touches.length > 1) this.finish(null, true);
      },
      { capture: true, passive: true },
    );
    listen(document, 'pointerup', (e) => this.finish(e, false));
    listen(document, 'pointercancel', (e) => this.finish(e, true));
    listen(window, 'pagehide', () => {
      const pointer = this.clearInput();
      if (pointer) this.onEnd(false);
      // Keep the selected media when BFCache freezes the page. Do not activate
      // a pending destination or start its media resources while leaving.
      controller.goTo(controller.index, true);
    });
    listen(viewport, 'lostpointercapture', (e) => {
      if (e.target === viewport) this.finish(e, true);
    });
    listen(
      viewport,
      'click',
      (e) => {
        if (this.gesture.cancelClick(e.detail, performance.now())) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      { capture: true },
    );
    listen(viewport, 'dragstart', (e) => e.preventDefault());
    listen(
      viewport,
      'wheel',
      (e) => {
        // 2.8.5 (Owner): vertical wheel and vertical swipes scroll the page and leave playing media alone;
        // media pauses on a slide change, or when the gallery leaves the screen or the page.
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || controller.count < 2) return;
        e.preventDefault();
        if (this.pointer) return;
        this.wheelDistance = (this.wheelDistance || 0) + e.deltaX;
        clearTimeout(this.wheelTimer);
        this.wheelTimer = setTimeout(() => {
          this.wheelDistance = 0;
        }, 160);
        if (Math.abs(this.wheelDistance) >= 24) {
          this.onNavigate(Math.sign(this.wheelDistance) * (controller.rtl ? -1 : 1));
          this.wheelDistance = 0;
        }
      },
      { passive: false },
    );
  }
  lock(x, y) {
    const p = this.pointer;
    if (!p || p.axis || p.generation !== this.controller.generation) return;
    const dx = Math.abs(x - p.x),
      dy = Math.abs(y - p.y);
    if (Math.max(dx, dy) <= 7) return;
    // Keep the existing vertical dominance threshold, but do not leave diagonal
    // travel pending until a large accumulated displacement suddenly catches up.
    p.axis = dy > dx * 1.2 ? 'y' : 'x';
  }
  recognize(x, y) {
    const p = this.pointer;
    if (!p || p.fired || p.generation !== this.controller.generation) return;
    this.lock(x, y);
    if (p.axis !== 'x' || Math.abs(x - p.x) < 30 || this.controller.count < 2) return;
    // Consume before navigation changes generation. Further movement, release
    // and compatibility touch events cannot issue a second action.
    p.fired = true;
    this.onNavigate(Math.sign(this.controller.sign * (x - p.x)));
  }
  finish(event, cancelled) {
    const p = this.pointer;
    if (!p || (event && event.pointerId !== p.id)) return;
    if (!cancelled && event) {
      this.gesture.move(p.id, event.clientX, event.clientY);
      this.recognize(event.clientX, event.clientY);
    }
    this.pointer = null;
    this.gesture.finish(p.id, performance.now(), cancelled);
    // Contact and release never stop or restart an animation. Only a new valid
    // swipe takes over through the exact same path as an arrow click.
    this.onEnd(p.fired);
    if (this.viewport.hasPointerCapture(p.id)) this.viewport.releasePointerCapture(p.id);
  }
  clearInput() {
    clearTimeout(this.wheelTimer);
    const pointer = this.pointer;
    this.pointer = null;
    if (pointer && this.viewport.hasPointerCapture(pointer.id)) this.viewport.releasePointerCapture(pointer.id);
    this.gesture.pointer = null;
    this.gesture.suppressUntil = 0;
    this.wheelDistance = 0;
    return pointer;
  }
  dispose() {
    this.abort.abort();
    this.clearInput();
  }
}
// One compositor track owns motion on every input device. Coordinates are in
// slide widths; only rendering converts them to pixels using cached geometry.
export class MediaTrack {
  constructor(
    viewport,
    slides,
    {
      onIndex,
      onBusy = () => {},
      reduced = () => false,
      preview,
      railClass = 'meri-slideshow__rail',
      railAttribute = 'data-slideshow-rail',
      duration = 350,
      rail = null,
    },
  ) {
    this.viewport = viewport;
    this.slides = slides;
    this.onIndex = onIndex;
    this.onBusy = onBusy;
    this.reduced = reduced;
    this.count = slides.length;
    this.index = 0;
    this.position = this.count > 1 ? 1 : 0;
    this.duration = duration;
    this.generation = 0;
    this.makePreview = preview;
    this.ownsRail = !rail;
    this.rail = rail || document.createElement('div');
    this.rail.className = railClass;
    this.rail.setAttribute(railAttribute, '');
    this.width = viewport.getBoundingClientRect().width;
    this.rtl = getComputedStyle(viewport).direction === 'rtl';
    this.sign = this.rtl ? 1 : -1;
    // Prepare optional enhancement content before moving the Liquid content.
    const leading = this.count > 1 ? this.preview(slides.at(-1)) : null;
    const trailing = this.count > 1 ? this.preview(slides[0]) : null;
    try {
      if (this.ownsRail) this.rail.append(...slides);
      if (leading) this.rail.prepend(leading);
      if (trailing) this.rail.append(trailing);
      [...this.rail.children].forEach((slide, index) => slide.style.setProperty('--slide-position', index));
      if (this.ownsRail) viewport.append(this.rail);
      this.paint(this.position);
    } catch (error) {
      this.dispose();
      throw error;
    }
  }
  matches(viewport, slides) {
    return (
      this.viewport === viewport &&
      this.rail.parentElement === viewport &&
      slides.length === this.slides.length &&
      slides.every((slide, index) => slide === this.slides[index] && slide.parentElement === this.rail)
    );
  }
  preview(slide) {
    if (this.makePreview) return this.makePreview(slide);
    // A copy of a slideshow slide whose pictures still wait (data-slide-deferred) is built without its files.
    const waiting = slide.hasAttribute?.('data-slide-deferred') && !slide.closest?.('[data-slides-ready]');
    const copy = waiting ? inertCopy(slide) : slide.cloneNode(true);
    for (const node of [copy, ...copy.querySelectorAll('*')]) {
      node.removeAttribute('id');
      node.removeAttribute('data-block-id');
      for (const name of node.getAttributeNames())
        if (name.startsWith('data-shopify-editor-')) node.removeAttribute(name);
    }
    copy.removeAttribute('data-slide');
    copy.setAttribute('data-slide-preview', '');
    copy.setAttribute('aria-hidden', 'true');
    copy.inert = true;
    for (const node of copy.querySelectorAll('video, iframe, model-viewer, template')) node.remove();
    // Loop copies stay ready for a seamless wrap but never compete with the hero (LCP) image. 2.9.1 (Owner): a copy of
    // a slideshow slide that still waits for its pictures waits with it; slideshow.js starts both.
    for (const image of copy.querySelectorAll('img')) {
      image.loading = waiting ? 'lazy' : 'eager';
      image.fetchPriority = 'low';
    }
    return copy;
  }
  transform(position) {
    return 'translate3d(' + this.sign * position * this.width + 'px,0,0)';
  }
  paint(position) {
    this.position = position;
    this.rail.style.transform = this.transform(position);
  }
  interrupt() {
    // Read the visible transform once, before any writes. Never restart an
    // interrupted transition from its already-selected logical destination.
    let position = this.position;
    if (this.animation && this.width) {
      const matrix = new DOMMatrixReadOnly(getComputedStyle(this.rail).transform);
      position = matrix.m41 / (this.sign * this.width);
    }
    this.generation++;
    this.animation?.cancel();
    this.animation = null;
    if (this.count > 1) {
      if (position < 1) position += this.count;
      else if (position > this.count) position -= this.count;
    }
    this.paint(position);
    return position;
  }
  move(direction) {
    if (this.count < 2) return;
    direction = Math.sign(direction);
    // Choose from what is predominantly visible, never from accumulated click
    // targets. A new input can pass the current destination without a queue.
    const position = this.interrupt();
    const target = Math.max(0, Math.min(this.count + 1, Math.round(position) + direction));
    this.animateTo(target, false, this.settleDuration(target));
  }
  goTo(index, instant = false) {
    const from = this.interrupt();
    const logical = wrapSlide(index, this.count);
    let target = this.count > 1 ? logical + 1 : 0;
    if (!instant && this.count > 1) {
      const candidates = [target, target - this.count, target + this.count].filter(
        (p) => p >= 0 && p <= this.count + 1,
      );
      target = candidates.reduce((best, p) => (Math.abs(p - from) < Math.abs(best - from) ? p : best), target);
    }
    this.animateTo(target, instant);
  }
  setIndex(index) {
    if (index === this.index && this.selected) return;
    this.index = index;
    this.selected = true;
    this.onIndex(index);
  }
  animateTo(position, instant = false, duration = this.duration, easing = 'cubic-bezier(0.22, 0.61, 0.36, 1)') {
    const from = this.position;
    const generation = ++this.generation;
    const index = wrapSlide(Math.round(position) - (this.count > 1 ? 1 : 0), this.count);
    this.onBusy(true);
    // Selection describes the destination immediately; animation only paints the journey.
    this.setIndex(index);
    const complete = () => {
      if (generation !== this.generation) return;
      this.animation = null;
      this.paint(this.count > 1 ? index + 1 : 0);
      this.onBusy(false);
    };
    if (instant || this.reduced() || !duration || Math.abs(position - from) < 0.0001) {
      complete();
      return;
    }
    this.paint(position);
    const animation = this.rail.animate(
      [{ transform: this.transform(from) }, { transform: this.transform(position) }],
      { duration, easing },
    );
    this.animation = animation;
    animation.onfinish = complete;
  }
  jump(index) {
    this.goTo(index, true);
  }
  settleDuration(target) {
    // A partial remaining slide must not restart a full slide's duration.
    // Ordinary one-slide arrows retain the configured duration and easing.
    return this.duration * Math.min(1, Math.abs(target - this.position));
  }
  resize(width) {
    if (!width || width === this.width) return;
    const target = this.position;
    const animation = this.animation;
    const timing = animation?.effect.getTiming();
    const remaining = animation ? Math.max(1, Number(timing.duration) - Number(animation.currentTime || 0)) : 0;
    const visible = animation
      ? new DOMMatrixReadOnly(getComputedStyle(this.rail).transform).m41 / (this.sign * this.width)
      : target;
    this.generation++;
    animation?.cancel();
    this.animation = null;
    this.width = width;
    this.paint(visible);
    if (animation) this.animateTo(target, false, remaining, timing.easing);
  }
  dispose() {
    this.animation?.cancel();
    this.animation = null;
    this.generation++;
    this.slides.forEach((slide) => slide.style.removeProperty('--slide-position'));
    if (this.ownsRail) {
      this.viewport.append(...this.slides.filter((slide) => slide.parentElement === this.rail));
      this.rail.remove();
    } else {
      for (const child of [...this.rail.children]) if (!this.slides.includes(child)) child.remove();
      this.rail.style.removeProperty('transform');
    }
  }
}
